/**
 * ComputedCollection — a typed, indexed collection of computed values.
 *
 * Used for multi-valued features like roots, extrema, and intersections
 * where the number of items may vary with the viewport or parameters.
 */

export interface ComputedCollectionItem {
  [property: string]: () => number;
}

export interface ComputedCollection {
  /** Number of items currently in the collection */
  getCount: () => number;
  /** Maximum number of items to register in ValueContext */
  maxItems: number;
  /** Get item at index, or undefined if out of range */
  getItem: (index: number) => ComputedCollectionItem | undefined;
  /** Property names available on each item (e.g. ['x', 'y']) */
  properties: readonly string[];
}

/**
 * Create an enumerated (viewport-scoped, left-to-right sorted) collection
 * from a getter that returns {x, y} points.
 */
export function createEnumeratedCollection(
  getItems: () => Array<{ x: number; y: number }>,
  maxItems: number,
): ComputedCollection {
  return {
    getCount: () => getItems().length,
    maxItems,
    getItem: (index: number) => {
      const items = getItems();
      if (index < 0 || index >= items.length) {
        return undefined;
      }
      const item = items[index];
      return {
        x: () => item.x,
        y: () => item.y,
      };
    },
    properties: ['x', 'y'],
  };
}

/**
 * Memoize a computation by cache key.
 * Returns a getter that recomputes only when the key changes.
 */
export function memoizeByKey<T>(compute: () => T, getCacheKey: () => string): () => T {
  let cachedKey: string | undefined;
  let cachedValue: T;

  return () => {
    const key = getCacheKey();
    if (key !== cachedKey) {
      cachedKey = key;
      cachedValue = compute();
    }
    return cachedValue;
  };
}
